import { isEmpty } from '@sgme/fp';
import classNames from 'classnames';
import { useFilterErrorForSidePanel } from 'components/common-form/sidePanel/status/validationErrors/useFilterErrorsForSidePanel';
import { lessAgressiveColorMapper } from 'components/PostTrade/ProductModification/components/ValidationErrors.utils';
import { LightAlert } from 'components/shared/LightAlert';
import UnderlyingValidationErrors from 'components/shared/underlyings/underlying-validation-errors';
import { useSPIntl } from 'hooks/useIntl';
import type { FormError, FormRecord } from 'models/formError';
import { translateValidationError } from 'utils/errors/form-validation-errors';

export interface ValidationErrorComponentProps<
  Form = FormRecord,
  Keys extends string = string,
  KeysPrefix extends string = string,
> {
  className?: string;
  validationErrors: readonly FormError<Form, Keys, KeysPrefix>[];
  hiddenFields?: readonly (keyof Form)[];
  onlyErrorsDisplayedInSummary?: boolean;
  errorPrefix: KeysPrefix;
}

export function ValidationErrorComponent<
  Form = FormRecord,
  Keys extends string = string,
  KeysPrefix extends string = string,
>({
  validationErrors,
  hiddenFields = [],
  onlyErrorsDisplayedInSummary = false,
  errorPrefix,
  className,
}: ValidationErrorComponentProps<Form, Keys, KeysPrefix>) {
  const intl = useSPIntl();

  const errorsToDisplay = useFilterErrorForSidePanel<Form, Keys, KeysPrefix>(
    validationErrors,
    onlyErrorsDisplayedInSummary,
    hiddenFields,
  );

  if (isEmpty(errorsToDisplay)) {
    return null;
  }

  return (
    <div data-e2e="validation-errors" className={classNames('validation-errors-component', className)}>
      {errorsToDisplay.map((error, index) => {
        const translatedValidationError = translateValidationError<Form, Keys, KeysPrefix>(error, intl, errorPrefix);
        const alertSeverity = translatedValidationError && lessAgressiveColorMapper(translatedValidationError.severity);
        const isLastError = index === errorsToDisplay.length - 1;

        return (
          <div
            className={classNames('d-flex flex-column bg-lvl1', { 'mb-1 pb-1': !isLastError })}
            key={`validation-error-${index}`}
          >
            {translatedValidationError && alertSeverity && (
              <LightAlert key={index} severity={alertSeverity} e2e="validation-warning">
                <span dangerouslySetInnerHTML={{ __html: translatedValidationError.message }} />
              </LightAlert>
            )}
            <UnderlyingValidationErrors
              underlyingValidationErrors={error.underlyingValidationErrors}
              severity={error.severity}
              errorPrefix={errorPrefix}
            />
          </div>
        );
      })}
    </div>
  );
}
